var _a;
import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import * as React from "react";
import { forwardRef, useState, useMemo, useCallback } from "react";
import { isDateRange, isDateInterval, isDateAfterType, isDateBeforeType, useDayRender, useActiveModifiers, Button, DayPicker, useDayPicker, useNavigation } from "react-day-picker";
import styled, { css } from "styled-components";
import { IconButton, TextButton } from "@creditornot/cb-button";
import { colors, typographyCss, cbSemanticCssVars } from "@creditornot/cb-ingredients";
import { MenuItemGroup, MenuActionItem } from "@creditornot/cb-menu";
import { Popover } from "@creditornot/cb-popover";
import { useDirection } from "@creditornot/cb-provider";
import { configureCssVars, createThemedCssVarDefinitions } from "@creditornot/cb-utils";
import { Input } from "@creditornot/cb-input";
function _typeof(o) {
  "@babel/helpers - typeof";
  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function(o2) {
    return typeof o2;
  } : function(o2) {
    return o2 && "function" == typeof Symbol && o2.constructor === Symbol && o2 !== Symbol.prototype ? "symbol" : typeof o2;
  }, _typeof(o);
}
function requiredArgs(required, args) {
  if (args.length < required) {
    throw new TypeError(required + " argument" + (required > 1 ? "s" : "") + " required, but only " + args.length + " present");
  }
}
function toDate(argument) {
  requiredArgs(1, arguments);
  var argStr = Object.prototype.toString.call(argument);
  if (argument instanceof Date || _typeof(argument) === "object" && argStr === "[object Date]") {
    return new Date(argument.getTime());
  } else if (typeof argument === "number" || argStr === "[object Number]") {
    return new Date(argument);
  } else {
    if ((typeof argument === "string" || argStr === "[object String]") && typeof console !== "undefined") {
      console.warn("Starting with v2.0.0-beta.1 date-fns doesn't accept strings as date arguments. Please use `parseISO` to parse strings. See: https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#string-arguments");
      console.warn(new Error().stack);
    }
    return /* @__PURE__ */ new Date(NaN);
  }
}
function startOfDay(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  date.setHours(0, 0, 0, 0);
  return date;
}
function isSameDay(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeftStartOfDay = startOfDay(dirtyDateLeft);
  var dateRightStartOfDay = startOfDay(dirtyDateRight);
  return dateLeftStartOfDay.getTime() === dateRightStartOfDay.getTime();
}
function toInteger(dirtyNumber) {
  if (dirtyNumber === null || dirtyNumber === true || dirtyNumber === false) {
    return NaN;
  }
  var number = Number(dirtyNumber);
  if (isNaN(number)) {
    return number;
  }
  return number < 0 ? Math.ceil(number) : Math.floor(number);
}
function addMilliseconds(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var timestamp = toDate(dirtyDate).getTime();
  var amount = toInteger(dirtyAmount);
  return new Date(timestamp + amount);
}
var MILLISECONDS_IN_MINUTE$1 = 6e4;
function addMinutes(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var amount = toInteger(dirtyAmount);
  return addMilliseconds(dirtyDate, amount * MILLISECONDS_IN_MINUTE$1);
}
function isDate(value) {
  requiredArgs(1, arguments);
  return value instanceof Date || _typeof(value) === "object" && Object.prototype.toString.call(value) === "[object Date]";
}
function isBefore(dirtyDate, dirtyDateToCompare) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var dateToCompare = toDate(dirtyDateToCompare);
  return date.getTime() < dateToCompare.getTime();
}
function isSameMonth(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeft = toDate(dirtyDateLeft);
  var dateRight = toDate(dirtyDateRight);
  return dateLeft.getFullYear() === dateRight.getFullYear() && dateLeft.getMonth() === dateRight.getMonth();
}
const SvgCalendar = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M17.5 0a1 1 0 011 1v1.75c0 .138.112.25.25.25h2.75a2 2 0 012 2v17a2 2 0 01-2 2h-19a2 2 0 01-2-2V5a2 2 0 012-2H4a.5.5 0 01.5.5v2.25a.75.75 0 001.5 0V1a1 1 0 112 0v1.751A.25.25 0 008.25 3h6.25a.5.5 0 01.5.5v2.25a.75.75 0 101.5 0V1a1 1 0 011-1zM21 9H3a.5.5 0 00-.5.5v12a.5.5 0 00.5.5h18a.5.5 0 00.5-.5v-12A.5.5 0 0021 9zM7.5 18.5a1 1 0 010 2h-1a1 1 0 010-2zm5 0a1 1 0 010 2h-1a1 1 0 010-2zm5 0a1 1 0 010 2h-1a1 1 0 010-2zm-10-4a1 1 0 010 2h-1a1 1 0 010-2zm5 0a1 1 0 010 2h-1a1 1 0 010-2zm5 0a1 1 0 010 2h-1a1 1 0 010-2zm-10-4a1 1 0 010 2h-1a1 1 0 010-2zm5 0a1 1 0 010 2h-1a1 1 0 010-2zm5 0a1 1 0 010 2h-1a1 1 0 010-2z" }) }));
const CalendarIcon = SvgCalendar;
const SvgCaretdown = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M12.028 19.5c.662 0 1.292-.283 1.73-.778l9.81-11.13a1.84 1.84 0 00-.195-2.568 1.85 1.85 0 00-2.578.134l-8.579 9.732a.25.25 0 01-.376 0L3.262 5.158a1.849 1.849 0 00-3.17.609A1.84 1.84 0 00.49 7.592l9.807 11.126a2.312 2.312 0 001.732.782z" }) }));
const CaretDownIcon = SvgCaretdown;
const SvgCaretleft = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M4.5 11.972c0-.662.283-1.292.778-1.73l11.13-9.81a1.84 1.84 0 012.568.195 1.85 1.85 0 01-.134 2.578L9.11 11.784a.25.25 0 000 .376l9.732 8.578a1.849 1.849 0 01-.609 3.17 1.84 1.84 0 01-1.825-.397L5.282 13.704a2.312 2.312 0 01-.782-1.732z" }) }));
const CaretLeftIcon = SvgCaretleft;
const SvgCaretright = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M19.5 11.972c0-.662-.283-1.292-.778-1.73L7.592.433a1.84 1.84 0 00-2.568.195 1.85 1.85 0 00.134 2.578l9.732 8.579a.25.25 0 010 .376l-9.732 8.578a1.849 1.849 0 00.609 3.17 1.84 1.84 0 001.825-.397l11.126-9.807a2.312 2.312 0 00.782-1.732z" }) }));
const CaretRightIcon = SvgCaretright;
const defaultDayPickerStyles = css`
  /* stylelint-disable declaration-no-important */
  /* Hide elements for devices that are not screen readers */
  .rdp-vhidden {
    box-sizing: border-box;
    margin: 0;
    background: transparent;
    appearance: none;
    position: absolute !important;
    top: 0;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    overflow: hidden !important;
    clip: rect(1px, 1px, 1px, 1px) !important;
    border: 0 !important;
  }

  /* Buttons */
  .rdp-button_reset {
    appearance: none;
    position: relative;
    margin: 0;
    padding: 0;
    cursor: default;
    color: inherit;
    background: none;
    font: inherit;
  }

  .rdp-button_reset:focus-visible {
    /* Make sure to reset outline only when :focus-visible is supported */
    outline: none;
  }

  .rdp-button {
    border: 2px solid transparent;
  }

  .rdp-button:not([disabled]) {
    cursor: pointer;
  }

  .rdp-months {
    display: flex;
  }

  .rdp-month {
    margin: 0 1em;
  }

  .rdp-month:first-child {
    margin-inline-start: 0;
  }

  .rdp-month:last-child {
    margin-inline-end: 0;
  }

  .rdp-table {
    margin: 0;
    border-collapse: collapse;
  }

  .rdp-head {
    border: 0;
  }

  .rdp-head_row,
  .rdp-row {
    height: 100%;
  }

  .rdp-head_cell {
    vertical-align: middle;
    font-size: 0.75em;
    font-weight: 700;
    text-align: center;
    padding: 0;
  }

  .rdp-tbody {
    border: 0;
  }

  .rdp-tfoot {
    margin: 0.5em;
  }

  .rdp-cell {
    padding: 0;
    text-align: center;
  }

  .rdp-weeknumber {
    font-size: 0.75em;
  }

  .rdp-weeknumber,
  .rdp-day {
    display: flex;
    overflow: hidden;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    margin: 0;
  }

  .rdp-day_today:not(.rdp-day_outside) {
    font-weight: 700;
  }
`;
function tzTokenizeDate(date, timeZone) {
  var dtf = getDateTimeFormat(timeZone);
  return dtf.formatToParts ? partsOffset(dtf, date) : hackyOffset(dtf, date);
}
var typeToPos = {
  year: 0,
  month: 1,
  day: 2,
  hour: 3,
  minute: 4,
  second: 5
};
function partsOffset(dtf, date) {
  try {
    var formatted = dtf.formatToParts(date);
    var filled = [];
    for (var i = 0; i < formatted.length; i++) {
      var pos = typeToPos[formatted[i].type];
      if (pos >= 0) {
        filled[pos] = parseInt(formatted[i].value, 10);
      }
    }
    return filled;
  } catch (error) {
    if (error instanceof RangeError) {
      return [NaN];
    }
    throw error;
  }
}
function hackyOffset(dtf, date) {
  var formatted = dtf.format(date).replace(/\u200E/g, "");
  var parsed = /(\d+)\/(\d+)\/(\d+),? (\d+):(\d+):(\d+)/.exec(formatted);
  return [parsed[3], parsed[1], parsed[2], parsed[4], parsed[5], parsed[6]];
}
var dtfCache = {};
function getDateTimeFormat(timeZone) {
  if (!dtfCache[timeZone]) {
    var testDateFormatted = new Intl.DateTimeFormat("en-US", {
      hour12: false,
      timeZone: "America/New_York",
      year: "numeric",
      month: "numeric",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    }).format(/* @__PURE__ */ new Date("2014-06-25T04:00:00.123Z"));
    var hourCycleSupported = testDateFormatted === "06/25/2014, 00:00:00" || testDateFormatted === "‎06‎/‎25‎/‎2014‎ ‎00‎:‎00‎:‎00";
    dtfCache[timeZone] = hourCycleSupported ? new Intl.DateTimeFormat("en-US", {
      hour12: false,
      timeZone,
      year: "numeric",
      month: "numeric",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    }) : new Intl.DateTimeFormat("en-US", {
      hourCycle: "h23",
      timeZone,
      year: "numeric",
      month: "numeric",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  }
  return dtfCache[timeZone];
}
function newDateUTC(fullYear, month, day, hour, minute, second, millisecond) {
  var utcDate = /* @__PURE__ */ new Date(0);
  utcDate.setUTCFullYear(fullYear, month, day);
  utcDate.setUTCHours(hour, minute, second, millisecond);
  return utcDate;
}
var MILLISECONDS_IN_HOUR = 36e5;
var MILLISECONDS_IN_MINUTE = 6e4;
var patterns = {
  timezone: /([Z+-].*)$/,
  timezoneZ: /^(Z)$/,
  timezoneHH: /^([+-]\d{2})$/,
  timezoneHHMM: /^([+-]\d{2}):?(\d{2})$/
};
function tzParseTimezone(timezoneString, date, isUtcDate) {
  var token;
  var absoluteOffset;
  if (!timezoneString) {
    return 0;
  }
  token = patterns.timezoneZ.exec(timezoneString);
  if (token) {
    return 0;
  }
  var hours;
  token = patterns.timezoneHH.exec(timezoneString);
  if (token) {
    hours = parseInt(token[1], 10);
    if (!validateTimezone(hours)) {
      return NaN;
    }
    return -(hours * MILLISECONDS_IN_HOUR);
  }
  token = patterns.timezoneHHMM.exec(timezoneString);
  if (token) {
    hours = parseInt(token[1], 10);
    var minutes = parseInt(token[2], 10);
    if (!validateTimezone(hours, minutes)) {
      return NaN;
    }
    absoluteOffset = Math.abs(hours) * MILLISECONDS_IN_HOUR + minutes * MILLISECONDS_IN_MINUTE;
    return hours > 0 ? -absoluteOffset : absoluteOffset;
  }
  if (isValidTimezoneIANAString(timezoneString)) {
    date = new Date(date || Date.now());
    var utcDate = isUtcDate ? date : toUtcDate(date);
    var offset = calcOffset(utcDate, timezoneString);
    var fixedOffset = isUtcDate ? offset : fixOffset(date, offset, timezoneString);
    return -fixedOffset;
  }
  return NaN;
}
function toUtcDate(date) {
  return newDateUTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds()
  );
}
function calcOffset(date, timezoneString) {
  var tokens = tzTokenizeDate(date, timezoneString);
  var asUTC = newDateUTC(
    tokens[0],
    tokens[1] - 1,
    tokens[2],
    tokens[3] % 24,
    tokens[4],
    tokens[5],
    0
  ).getTime();
  var asTS = date.getTime();
  var over = asTS % 1e3;
  asTS -= over >= 0 ? over : 1e3 + over;
  return asUTC - asTS;
}
function fixOffset(date, offset, timezoneString) {
  var localTS = date.getTime();
  var utcGuess = localTS - offset;
  var o2 = calcOffset(new Date(utcGuess), timezoneString);
  if (offset === o2) {
    return offset;
  }
  utcGuess -= o2 - offset;
  var o3 = calcOffset(new Date(utcGuess), timezoneString);
  if (o2 === o3) {
    return o2;
  }
  return Math.max(o2, o3);
}
function validateTimezone(hours, minutes) {
  return -23 <= hours && hours <= 23 && (minutes == null || 0 <= minutes && minutes <= 59);
}
var validIANATimezoneCache = {};
function isValidTimezoneIANAString(timeZoneString) {
  if (validIANATimezoneCache[timeZoneString])
    return true;
  try {
    new Intl.DateTimeFormat(void 0, { timeZone: timeZoneString });
    validIANATimezoneCache[timeZoneString] = true;
    return true;
  } catch (error) {
    return false;
  }
}
function getTimezoneOffset(timeZone, date) {
  return -tzParseTimezone(timeZone, date);
}
const defaultLocale = typeof window === "object" && ((_a = window.navigator) == null ? void 0 : _a.language) ? window.navigator.language : "en-GB";
const isDateFnsLocale = (value) => typeof value === "object" && !!value.code;
const getLocaleCode = (locale) => {
  return isDateFnsLocale(locale) ? locale.code : locale || defaultLocale;
};
const getDiff = (date, timezone) => {
  return date.getTimezoneOffset() - getTimezoneOffset(timezone, date) / -6e4;
};
const createDateShiftedToTimezone = (date, timezone) => {
  return addMinutes(date, getDiff(date, timezone));
};
const createDateShiftedFromTimezone = (date, timezone) => {
  return addMinutes(date, getDiff(date, timezone) * -1);
};
function isDateType(value) {
  return isDate(value);
}
const createReactDayPickerValueForTimezone = (value, timezone) => {
  if (isDateType(value))
    return createDateShiftedToTimezone(value, timezone);
  if (Array.isArray(value))
    return value.map(
      (matcher) => createReactDayPickerValueForTimezone(matcher, timezone)
    );
  if (isDateRange(value))
    return {
      from: value.from ? createDateShiftedToTimezone(value.from, timezone) : void 0,
      to: value.to ? createDateShiftedToTimezone(value.to, timezone) : void 0
    };
  if (isDateInterval(value))
    return {
      before: createDateShiftedToTimezone(value.before, timezone),
      after: createDateShiftedToTimezone(value.after, timezone)
    };
  if (isDateAfterType(value))
    return { after: createDateShiftedToTimezone(value.after, timezone) };
  if (isDateBeforeType(value))
    return { before: createDateShiftedToTimezone(value.before, timezone) };
  if (value instanceof Function)
    return (date) => value(createDateShiftedToTimezone(date, timezone));
  return value;
};
const { cssString: colorCssString, cssVars: colorCssVars } = configureCssVars({
  dayBgSelected: {
    light: colors.wolt100,
    lightHighContrast: colors.darkSky100,
    dark: colors.wolt100
  },
  dayTextSelected: {
    light: colors.salt100,
    dark: colors.pepper100
  },
  dayBgHover: {
    light: colors.pepper8,
    dark: colors.salt8
  },
  dayBgActive: {
    light: colors.pepper12,
    dark: colors.salt12
  },
  outsideDayBgSelected: {
    light: colors.pepper8,
    dark: colors.salt8
  },
  dayBgSelectedHover: {
    light: colors.wolt92,
    lightHighContrast: colors.darkSky92,
    dark: colors.wolt92
  },
  dayBgSelectedPressed: {
    light: colors.wolt84,
    lightHighContrast: colors.darkSky84,
    dark: colors.wolt84
  }
});
const fontCss = css`
  ${typographyCss.baseFontFamily()}
  font-size: 14px;
  line-height: 20px;
  font-style: normal;
  font-stretch: normal;
`;
const Root$1 = styled.div`
  ${defaultDayPickerStyles}
  ${colorCssString}

  color: ${cbSemanticCssVars.textPrimary};
  ${fontCss}

  position: relative;
  z-index: 0;

  &,
  .rdp {
    width: ${({ $spacing }) => $spacing === "compact" ? "252px" : "324px"};
  }

  .rdp {
    position: static;
    background: transparent;
    user-select: none;
  }

  .rdp-month {
    z-index: 1;
    margin: 0;
    display: block;
  }

  .rdp-day_today {
    color: ${colorCssVars.dayBgSelected};
  }

  .rdp-head_cell {
    box-sizing: border-box;
    min-width: 36px;
    height: 28px;
    padding: 4px 8px;
    ${fontCss}
    font-weight: 400;
    text-align: center;
    color: ${cbSemanticCssVars.textSecondary};

    ${({ $spacing }) => $spacing === "spacious" && css`
        &:first-child {
          padding-inline-start: 2px;
        }

        &:last-child {
          padding-inline-end: 2px;
        }
      `}
  }

  .rdp-day {
    position: relative;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 16px;
    margin-bottom: ${({ $spacing }) => $spacing === "compact" ? 0 : "12px"};
    margin-inline-start: ${({ $spacing }) => $spacing === "compact" ? 0 : "6px"};
    margin-inline-end: ${({ $spacing }) => $spacing === "compact" ? 0 : "6px"};
    min-width: 36px;
    height: ${({ $spacing }) => $spacing === "compact" ? "28px" : "24px"};
    overflow: visible;
    color: ${cbSemanticCssVars.textPrimary};
    border-radius: 8px;
    ${fontCss}
    text-align: center;

    :focus:not(:focus-visible) {
      outline: none;
    }
    :focus-visible {
      outline: 2px transparent solid;
      box-shadow: 0 0 0 2px ${cbSemanticCssVars.a11yFocusIndicator};
    }

    &.rdp-day_disabled {
      color: ${cbSemanticCssVars.textTertiary};
      cursor: not-allowed;
    }
  }

  .rdp-cell:first-child .rdp-day {
    margin-inline-start: 0;
  }

  .rdp-cell:last-child .rdp-day {
    margin-inline-end: 0;
  }

  .rdp-day_outside {
    color: ${cbSemanticCssVars.textTertiary};

    ${({ $enableOutsideDaysClick }) => $enableOutsideDaysClick && css`
        cursor: pointer;

        &:hover {
          background-color: ${colorCssVars.dayBgHover};
          color: ${cbSemanticCssVars.textPrimary};
        }

        &:active {
          background-color: ${colorCssVars.dayBgActive};
        }
      `}

    &.rdp-day_selected {
      background-color: ${colorCssVars.outsideDayBgSelected};
    }
  }

  .rdp-day_selected:not(.rdp-day_disabled):not(.rdp-day_outside) {
    font-weight: 700;
    background-color: ${colorCssVars.dayBgSelected};
    color: ${colorCssVars.dayTextSelected};

    cursor: pointer;

    &:hover {
      background-color: ${colorCssVars.dayBgSelectedHover};
    }

    &:active {
      background-color: ${colorCssVars.dayBgSelectedPressed};
    }
  }

  .rdp-day:not(.rdp-day_disabled):not(.rdp-day_selected):not(.rdp-day_outside):hover {
    background-color: ${colorCssVars.dayBgHover};

    &:active {
      background-color: ${colorCssVars.dayBgActive};
    }
  }
`;
const HEADER_HEIGHT = 52;
const HeaderPlaceHolder = styled.div`
  height: ${HEADER_HEIGHT}px;
`;
const MonthHeader = styled.div`
  z-index: 1;
  position: absolute;
  top: 0;
  left: 24px;
  right: 24px;

  ${fontCss}
  display: flex;
  align-items: center;
  justify-content: center;
`;
const MonthTitle = styled.div`
  text-align: center;
  height: ${HEADER_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: capitalize;
`;
const NavigationContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: ${HEADER_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const CaretLeft = styled(CaretLeftIcon)`
  [dir='rtl'] & {
    transform: rotate(180deg);
  }
`;
const CaretRight = styled(CaretRightIcon)`
  [dir='rtl'] & {
    transform: rotate(180deg);
  }
`;
const Caption = ({
  disableMonthChange,
  disableYearSelect,
  displayMonth,
  locale,
  onYearChange,
  years
}) => {
  const { dir, numberOfMonths } = useDayPicker();
  const { displayMonths, goToMonth, nextMonth, previousMonth } = useNavigation();
  const displayIndex = displayMonths.findIndex((month) => isSameMonth(displayMonth, month));
  let isFirst = displayIndex === 0;
  let isLast = displayIndex === displayMonths.length - 1;
  if (dir === "rtl") {
    [isLast, isFirst] = [isFirst, isLast];
  }
  const hideNext = disableMonthChange || numberOfMonths > 1 && (isFirst || !isLast);
  const hidePrevious = disableMonthChange || numberOfMonths > 1 && (isLast || !isFirst);
  const handlePreviousClick = () => {
    if (!previousMonth)
      return;
    goToMonth(previousMonth);
  };
  const handleNextClick = () => {
    if (!nextMonth)
      return;
    goToMonth(nextMonth);
  };
  if (!nextMonth && !previousMonth) {
    return null;
  }
  return /* @__PURE__ */ jsxs(NavigationContainer, { children: [
    /* @__PURE__ */ jsx(
      DatePickerHeader,
      {
        date: displayMonth,
        disableYearSelect,
        locale,
        onYearChange,
        years
      }
    ),
    !hidePrevious && /* @__PURE__ */ jsx(
      IconButton,
      {
        "aria-label": "Go to previous month",
        background: "animated",
        disabled: !previousMonth,
        name: "previous-month",
        onClick: handlePreviousClick,
        size: "tiny",
        variant: "black",
        children: /* @__PURE__ */ jsx(CaretLeft, {})
      }
    ),
    !hideNext && /* @__PURE__ */ jsx(
      IconButton,
      {
        "aria-label": "Go to next month",
        background: "animated",
        disabled: !nextMonth,
        name: "next-month",
        onClick: handleNextClick,
        size: "tiny",
        variant: "black",
        children: /* @__PURE__ */ jsx(CaretRight, {})
      }
    )
  ] });
};
const YearSelectContainer = styled.div`
  margin-inline-start: 8px;
`;
const SelectYearPopoverContent = styled.div`
  background-color: ${cbSemanticCssVars.surfaceMain};
  border-radius: 8px;
  padding: 8px 16px;
  max-height: 248px;
  overflow-y: auto;
`;
const DotIndicator = styled.div`
  ${({ $indicatorColor }) => createThemedCssVarDefinitions({
  dotColor: $indicatorColor
})}
  position: absolute;
  bottom: -14px;
  left: 50%;
  /* stylelint-disable-next-line property-disallowed-list */
  margin-left: -2px;

  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: ${({ $indicatorColor }) => typeof $indicatorColor === "string" ? $indicatorColor : "var(--dotColor)"};
`;
const DatePickerHeader = ({
  date,
  disableYearSelect,
  locale,
  onYearChange,
  years
}) => {
  const IntlMonthFormat = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: "long" }),
    [locale]
  );
  const IntlYearFormat = useMemo(
    () => new Intl.DateTimeFormat(locale, { year: "numeric" }),
    [locale]
  );
  const yearsToSelect = useMemo(() => {
    if (years) {
      return years;
    }
    const now = date || /* @__PURE__ */ new Date();
    const yearNow = now.getFullYear();
    return [yearNow - 3, yearNow - 2, yearNow - 1, yearNow, yearNow + 1, yearNow + 2];
  }, [date, years]);
  return /* @__PURE__ */ jsxs(MonthHeader, { children: [
    /* @__PURE__ */ jsx(MonthTitle, { children: IntlMonthFormat.format(date) }),
    !disableYearSelect && /* @__PURE__ */ jsx(YearSelectContainer, { children: /* @__PURE__ */ jsx(
      Popover,
      {
        content: /* @__PURE__ */ jsx(SelectYearPopoverContent, { children: /* @__PURE__ */ jsx(MenuItemGroup, { children: yearsToSelect.map((year) => /* @__PURE__ */ jsx(
          MenuActionItem,
          {
            onClick: () => {
              onYearChange(new Date(year, date.getMonth()));
            },
            selected: year === date.getFullYear(),
            variant: "black",
            children: year
          },
          year
        )) }) }),
        placement: "bottom-start",
        renderArrow: false,
        rendering: "inline",
        children: ({ refProps }) => /* @__PURE__ */ jsx(
          TextButton,
          {
            ...refProps,
            icon: /* @__PURE__ */ jsx(CaretDownIcon, {}),
            iconPosition: "right",
            variant: "grey",
            children: IntlYearFormat.format(date)
          }
        )
      }
    ) })
  ] });
};
const DatePickerBase = ({
  className,
  disabledDays,
  disableMonthChange = false,
  disableOutsideDaysClick = false,
  disableYearSelect = false,
  firstDayOfWeek,
  initialMonth = /* @__PURE__ */ new Date(),
  locale = defaultLocale,
  onDayClick,
  renderDayDotIndicator,
  selectedDays,
  showOutsideDays = true,
  style,
  timezone,
  years,
  ...rest
}) => {
  const direction = useDirection();
  const [selectedMonth, setSelectedMonth] = useState(
    timezone ? createDateShiftedToTimezone(initialMonth, timezone) : initialMonth
  );
  const localeCode = getLocaleCode(locale) || defaultLocale;
  const weekStartsOn = useMemo(() => {
    var _a2;
    if (firstDayOfWeek !== void 0)
      return firstDayOfWeek;
    return isDateFnsLocale(locale) ? (_a2 = locale.options) == null ? void 0 : _a2.weekStartsOn : void 0;
  }, [locale, firstDayOfWeek]);
  const handleDayClick = (day, modifiers2) => {
    if (onDayClick && !modifiers2.disabled && !(disableOutsideDaysClick && modifiers2.outside)) {
      onDayClick(timezone ? createDateShiftedFromTimezone(day, timezone) : day);
    }
  };
  const selectedDaysAdjusted = useMemo(() => {
    if (!timezone || !selectedDays)
      return selectedDays;
    return createReactDayPickerValueForTimezone(selectedDays, timezone);
  }, [timezone, selectedDays]);
  const disabledDaysAdjusted = useMemo(() => {
    if (!timezone || !disabledDays)
      return disabledDays;
    return createReactDayPickerValueForTimezone(disabledDays, timezone);
  }, [timezone, disabledDays]);
  const formatters = useMemo(() => {
    const { format } = new Intl.DateTimeFormat(localeCode, { weekday: "narrow" });
    return { formatWeekdayName: (date) => format(date).toUpperCase() };
  }, [localeCode]);
  const labels = useMemo(() => {
    const { format } = new Intl.DateTimeFormat(localeCode, { weekday: "long" });
    return { labelWeekday: (date) => format(date) };
  }, [localeCode]);
  const customComponents = useMemo(() => {
    const { format } = new Intl.DateTimeFormat(localeCode, { day: "numeric" });
    return {
      Caption: (props) => /* @__PURE__ */ jsx(
        Caption,
        {
          ...props,
          disableMonthChange,
          disableYearSelect,
          locale: localeCode,
          onYearChange: setSelectedMonth,
          years
        }
      ),
      Day: (props) => {
        const buttonRef = React.useRef(null);
        const dayRender = useDayRender(props.date, props.displayMonth, buttonRef);
        const { outside } = useActiveModifiers(props.date, props.displayMonth);
        if (dayRender.isHidden) {
          return /* @__PURE__ */ jsx("div", { role: "gridcell" });
        }
        if (!dayRender.isButton || disableOutsideDaysClick && outside) {
          return /* @__PURE__ */ jsx("div", { ...dayRender.divProps });
        }
        return /* @__PURE__ */ jsx(Button, { name: "day", ref: buttonRef, ...dayRender.buttonProps });
      },
      DayContent: ({ date }) => {
        const indicatorColor = renderDayDotIndicator && renderDayDotIndicator(date);
        return /* @__PURE__ */ jsxs(Fragment, { children: [
          format(date),
          !!indicatorColor && /* @__PURE__ */ jsx(DotIndicator, { $indicatorColor: indicatorColor })
        ] });
      }
    };
  }, [
    localeCode,
    disableOutsideDaysClick,
    disableMonthChange,
    disableYearSelect,
    renderDayDotIndicator,
    years
  ]);
  const modifiers = useMemo(
    () => ({
      today: (date) => isSameDay(/* @__PURE__ */ new Date(), timezone ? createDateShiftedFromTimezone(date, timezone) : date)
    }),
    [timezone]
  );
  return /* @__PURE__ */ jsxs(
    Root$1,
    {
      $enableOutsideDaysClick: !disableOutsideDaysClick && showOutsideDays,
      $spacing: renderDayDotIndicator ? "spacious" : "compact",
      className,
      dir: direction,
      style,
      children: [
        /* @__PURE__ */ jsx(HeaderPlaceHolder, {}),
        /* @__PURE__ */ jsx(
          DayPicker,
          {
            ...rest,
            components: customComponents,
            disabled: disabledDaysAdjusted,
            formatters,
            labels,
            locale: isDateFnsLocale(locale) ? locale : void 0,
            modifiers,
            month: selectedMonth,
            onDayClick: handleDayClick,
            onMonthChange: setSelectedMonth,
            selected: (
              // Typing here seems problematic with `mode` coming from props,
              // see https://github.com/gpbl/react-day-picker/issues/1583
              selectedDaysAdjusted
            ),
            showOutsideDays,
            weekStartsOn
          }
        )
      ]
    }
  );
};
const DatePicker = ({ initialMonth, onChange, timezone, value, ...rest }) => {
  const selectedDays = useMemo(() => value ? [value] : void 0, [value]);
  const handleChange = (day) => {
    if (onChange) {
      onChange(value && isSameDay(day, value) ? void 0 : day);
    }
  };
  return /* @__PURE__ */ jsx(
    DatePickerBase,
    {
      initialMonth: (selectedDays && selectedDays[0]) ?? initialMonth,
      onDayClick: handleChange,
      selectedDays,
      timezone,
      ...rest
    }
  );
};
const Root = styled.div`
  display: inline-block;
`;
const DatePickerPopover = ({
  children,
  className,
  content,
  contentZIndex,
  disabled,
  onCloseStart,
  placement = "bottom-start",
  portalElement,
  rendering,
  style
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  return /* @__PURE__ */ jsx(
    Popover,
    {
      content: typeof content === "function" ? content({
        disabled,
        closePopover: () => setPopoverOpen(false),
        openPopover: () => setPopoverOpen(true),
        isOpen: popoverOpen
      }) : content,
      offset: 4,
      onCloseStart,
      onOpenChange: setPopoverOpen,
      open: disabled ? false : popoverOpen,
      placement,
      portalElement,
      renderArrow: false,
      rendering,
      zIndex: contentZIndex,
      children: ({ refProps }) => /* @__PURE__ */ jsx(Root, { role: "group", ...refProps, className, style, children: children({
        disabled,
        isOpen: popoverOpen,
        closePopover: () => setPopoverOpen(false),
        openPopover: () => setPopoverOpen(true)
      }) })
    }
  );
};
const StyledDatePickerPopover = styled(DatePickerPopover)`
  width: ${({ $stretch }) => $stretch ? "100%" : "auto"};
`;
const DatePickerContainer$2 = styled.div`
  padding: 4px 16px 20px 16px;
`;
const DatePickerInput = ({
  children,
  className,
  closeOnChange,
  disableClear,
  disabled,
  disabledDays,
  disableMonthChange,
  disableOutsideDaysClick,
  disableYearSelect,
  formatValue,
  initialMonth,
  inputIcon = /* @__PURE__ */ jsx(CalendarIcon, {}),
  inputId,
  inputName,
  inputRef,
  invalid,
  locale = defaultLocale,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  popoverPlacement,
  popoverPortalElement,
  popoverRendering,
  popoverZIndex,
  renderDayDotIndicator,
  rootRef,
  showOutsideDays,
  stretch = false,
  style,
  timezone,
  value,
  years
}, ref) => {
  const IntlDateTimeFormat = useMemo(() => {
    const options = timezone ? { timeZone: timezone } : void 0;
    return new Intl.DateTimeFormat(getLocaleCode(locale), options);
  }, [locale, timezone]);
  const formatInputValue = formatValue || IntlDateTimeFormat.format;
  const formattedValue = value ? formatInputValue(value, timezone ? { timeZone: timezone } : void 0) : "";
  return /* @__PURE__ */ jsx(
    StyledDatePickerPopover,
    {
      $stretch: stretch,
      content: ({ closePopover }) => /* @__PURE__ */ jsx(DatePickerContainer$2, { children: /* @__PURE__ */ jsx(
        DatePicker,
        {
          disableMonthChange,
          disableOutsideDaysClick,
          disableYearSelect,
          disabledDays,
          initialMonth,
          locale,
          onChange: (day) => {
            if (onChange) {
              onChange(day);
            }
            if (closeOnChange) {
              closePopover();
            }
          },
          renderDayDotIndicator,
          showOutsideDays,
          timezone,
          value,
          years
        }
      ) }),
      contentZIndex: popoverZIndex,
      disabled,
      onCloseStart: onBlur,
      placement: popoverPlacement,
      portalElement: popoverPortalElement,
      rendering: popoverRendering,
      children: children ? (props) => children({ ...props, value, formattedValue }) : () => /* @__PURE__ */ jsx(
        Input,
        {
          className,
          disabled,
          icon: inputIcon,
          id: inputId,
          invalid,
          name: inputName,
          onClearClick: disableClear ? void 0 : (event) => {
            event.stopPropagation();
            if (onChange) {
              onChange(void 0);
            }
          },
          onFocus,
          placeholder,
          readOnly: true,
          ref: ref ?? inputRef,
          rootRef,
          stretch,
          style,
          value: formattedValue
        }
      )
    }
  );
};
const DatePickerInput$1 = forwardRef(DatePickerInput);
const DateMultiPicker = ({ onChange, value = [], ...rest }) => {
  return /* @__PURE__ */ jsx(
    DatePickerBase,
    {
      ...rest,
      initialMonth: value[0],
      onDayClick: (day) => {
        if (onChange) {
          const selectedDay = value.find((d) => isSameDay(d, day));
          onChange(selectedDay ? value.filter((v) => v !== selectedDay) : [...value, day]);
        }
      },
      selectedDays: value
    }
  );
};
const EMPTY_SELECTION = [];
const DatePickerContainer$1 = styled.div`
  padding: 4px 16px 20px 16px;
`;
const DateMultiPickerInput = ({
  children,
  className,
  disableClear,
  disabled,
  disabledDays,
  disableMonthChange,
  disableOutsideDaysClick,
  disableYearSelect,
  formatValue,
  inputIcon = /* @__PURE__ */ jsx(CalendarIcon, {}),
  invalid,
  locale = defaultLocale,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  popoverPlacement,
  popoverZIndex,
  rootRef,
  showOutsideDays,
  style,
  timezone,
  value
}, ref) => {
  const IntlDateTimeFormat = useMemo(() => {
    const options = timezone ? { timeZone: timezone } : void 0;
    return new Intl.DateTimeFormat(getLocaleCode(locale), options);
  }, [locale, timezone]);
  const defaultFormatValue = useCallback(
    (value2) => {
      if (value2.length === 0) {
        return "";
      }
      if (value2.length === 1) {
        return IntlDateTimeFormat.format(value2[0]);
      }
      return `${value2.length} dates selected`;
    },
    [IntlDateTimeFormat]
  );
  const formattedValue = value && value.length > 0 ? (formatValue || defaultFormatValue)(value, { timeZone: timezone }) : "";
  return /* @__PURE__ */ jsx(
    DatePickerPopover,
    {
      content: /* @__PURE__ */ jsx(DatePickerContainer$1, { children: /* @__PURE__ */ jsx(
        DateMultiPicker,
        {
          disableMonthChange,
          disableOutsideDaysClick,
          disableYearSelect,
          disabledDays,
          locale,
          onChange,
          showOutsideDays,
          timezone,
          value
        }
      ) }),
      contentZIndex: popoverZIndex,
      onCloseStart: onBlur,
      placement: popoverPlacement,
      children: children ? (props) => children({ ...props, value, formattedValue }) : () => /* @__PURE__ */ jsx(
        Input,
        {
          className,
          disabled,
          icon: inputIcon,
          invalid,
          onClearClick: disableClear ? void 0 : (event) => {
            event.stopPropagation();
            if (onChange) {
              onChange(EMPTY_SELECTION);
            }
          },
          onFocus,
          placeholder,
          readOnly: true,
          ref,
          rootRef,
          style,
          value: formattedValue
        }
      )
    }
  );
};
const DateMultiPickerInput$1 = forwardRef(DateMultiPickerInput);
const { cssString, cssVars } = configureCssVars({
  dayMiddleBgSelected: {
    light: colors.wolt8,
    lightHighContrast: colors.darkSky8,
    dark: colors.wolt8
  },
  dayMiddleBgHover: {
    light: colors.wolt16,
    lightHighContrast: colors.darkSky16,
    dark: colors.wolt16
  },
  dayMiddleTextSelected: {
    light: colors.wolt100,
    lightHighContrast: colors.darkSky100,
    dark: colors.wolt100
  }
});
const StyledDatePickerBase = styled(DatePickerBase)`
  ${cssString}

  .rdp-day_range_start {
    border-start-end-radius: 0;
    border-end-end-radius: 0;
  }

  .rdp-day_range_end:not(.rdp-day_range_start) {
    border-start-start-radius: 0;
    border-end-start-radius: 0;
  }

  .rdp-day_selected.rdp-day_outside {
    &.rdp-day_range_start,
    &.rdp-day_range_end {
      background-color: ${cbSemanticCssVars.textSecondary};
      color: ${colorCssVars.dayTextSelected};
    }
  }

  .rdp-day_selected.rdp-day_range_middle {
    border-radius: 0;

    &:not(.rdp-day_outside) {
      background-color: ${cssVars.dayMiddleBgSelected};
      color: ${cssVars.dayMiddleTextSelected};

      &:not(.rdp-day_disabled):hover {
        background-color: ${cssVars.dayMiddleBgHover};
      }
    }

    &.rdp-day_outside {
      background-color: ${colorCssVars.outsideDayBgSelected};
    }
  }
`;
const DateRangePicker = ({ onChange, onDayClick, value = {}, ...rest }) => {
  const [lastSelection, setLastSelection] = useState();
  const { from, to } = value;
  return /* @__PURE__ */ jsx(
    StyledDatePickerBase,
    {
      ...rest,
      initialMonth: value.from || value.to,
      mode: "range",
      onDayClick: (day) => {
        if (onDayClick) {
          onDayClick(day);
        }
        if (!onChange) {
          return;
        }
        if (!from || lastSelection === void 0 || lastSelection === "to") {
          setLastSelection("from");
          return onChange({
            from: day,
            to: to && to >= day ? to : void 0
          });
        }
        if (from && day < from) {
          setLastSelection("to");
          return onChange({ from: day, to: from });
        }
        if (from && day >= from) {
          setLastSelection("to");
          return onChange({ from, to: day });
        }
      },
      selectedDays: value
    }
  );
};
const DatePickerContainer = styled.div`
  padding: 4px 16px 20px 16px;
`;
const StyledInput = styled(Input)`
  min-width: ${({ onClearClick }) => onClearClick ? 240 : 220}px;
`;
const DateRangePickerInput = ({
  children,
  className,
  disableClear,
  disabled,
  disabledDays,
  disableMonthChange,
  disableOutsideDaysClick,
  disableYearSelect,
  formatValue,
  inputIcon = /* @__PURE__ */ jsx(CalendarIcon, {}),
  inputRef,
  invalid,
  locale = defaultLocale,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  popoverPlacement,
  popoverPortalElement,
  popoverRendering,
  popoverZIndex,
  renderDayDotIndicator,
  rootRef,
  showOutsideDays,
  style,
  timezone,
  value
}, ref) => {
  const IntlDateTimeFormat = useMemo(() => {
    const options = timezone ? { timeZone: timezone } : void 0;
    return new Intl.DateTimeFormat(getLocaleCode(locale), options);
  }, [locale, timezone]);
  const [firstSelection, setFirstSelection] = useState(void 0);
  const valueToShow = useMemo(
    () => firstSelection ? { from: firstSelection } : value,
    [firstSelection, value]
  );
  const defaultFormatValue = useCallback(
    (value2) => {
      if (!value2.from && !value2.to) {
        return "";
      }
      return `${value2.from ? IntlDateTimeFormat.format(value2.from) : ""} – ${value2.to ? IntlDateTimeFormat.format(value2.to) : ""}`;
    },
    [IntlDateTimeFormat]
  );
  const formattedValue = value ? (formatValue || defaultFormatValue)(value, { timeZone: timezone }) : "";
  return /* @__PURE__ */ jsx(
    DatePickerPopover,
    {
      content: ({ closePopover }) => /* @__PURE__ */ jsx(DatePickerContainer, { children: /* @__PURE__ */ jsx(
        DateRangePicker,
        {
          disableMonthChange,
          disableOutsideDaysClick,
          disableYearSelect,
          disabledDays,
          locale,
          onDayClick: (day) => {
            if (firstSelection === void 0) {
              setFirstSelection(day);
            } else {
              onChange(
                isBefore(firstSelection, day) ? { from: firstSelection, to: day } : { from: day, to: firstSelection }
              );
              setFirstSelection(void 0);
              closePopover();
            }
          },
          renderDayDotIndicator,
          showOutsideDays,
          timezone,
          value: valueToShow
        }
      ) }),
      contentZIndex: popoverZIndex,
      disabled,
      onCloseStart: onBlur,
      placement: popoverPlacement,
      portalElement: popoverPortalElement,
      rendering: popoverRendering,
      children: children ? (props) => children({ ...props, value, formattedValue }) : () => /* @__PURE__ */ jsx(
        StyledInput,
        {
          className,
          disabled,
          icon: inputIcon,
          invalid,
          onClearClick: disableClear ? void 0 : () => {
            if (onChange) {
              onChange(void 0);
            }
          },
          onFocus: (event) => {
            setFirstSelection(void 0);
            if (onFocus) {
              onFocus(event);
            }
          },
          placeholder,
          readOnly: true,
          ref: ref ?? inputRef,
          rootRef,
          style,
          value: formattedValue
        }
      )
    }
  );
};
const DateRangePickerInput$1 = forwardRef(DateRangePickerInput);
export {
  DateMultiPicker,
  DateMultiPickerInput$1 as DateMultiPickerInput,
  DatePicker,
  DatePickerInput$1 as DatePickerInput,
  DateRangePicker,
  DateRangePickerInput$1 as DateRangePickerInput
};
//# sourceMappingURL=index.js.map
